import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Globe, Sparkles, Upload, CheckCircle2, AlertCircle, ImageIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const MAHARERA_RE = /^P\d{8,13}$/;
const CONSTRUCTION_OPTIONS = [
  { value: "PRE_LAUNCH", label: "Pre-launch" },
  { value: "UNDER_CONSTRUCTION", label: "Under construction" },
  { value: "READY_TO_MOVE", label: "Ready to move" },
];

const ALL_LOCALITIES_WEST = ["Baner", "Balewadi", "Wakad", "Tathawade", "Hinjewadi"];
const ALL_LOCALITIES_EAST = ["Kharadi", "Viman Nagar", "Wagholi", "Magarpatta", "Mundhwa"];

// Sentinel used in the shadcn <Select> because "" is treated as "no value" —
// we need a real string for the "no agent" option.
const NO_AGENT = "__none__";

export default function AdminBuilderIngest() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [scrape, setScrape] = useState(null); // {extracted, hero_image_url, hero_image_candidates}
  const [form, setForm] = useState(null);
  const [ingesting, setIngesting] = useState(false);
  const [heroPreview, setHeroPreview] = useState(null);
  const [uploadingHero, setUploadingHero] = useState(false);
  const heroRef = useRef(null);

  // Optional agent-assignment dropdown data (fetched once)
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    api.get("/admin/agents")
      .then(({ data }) => setAgents((data.agents || []).filter((a) => a.slug)))
      .catch(() => {});
  }, []);

  const onScrape = async () => {
    const u = url.trim();
    if (!/^https?:\/\//i.test(u)) return toast.error("URL must start with http:// or https://");
    setScraping(true);
    setScrape(null);
    setForm(null);
    setHeroPreview(null);
    try {
      const { data } = await api.post("/admin/builder-scrape", { url: u });
      setScrape(data);
      setHeroPreview(data.hero_image_url || null);
      setForm({
        project_name: data.extracted.project_name || "",
        maharera_id: data.extracted.maharera_id || "",
        developer: data.extracted.developer || "",
        starting_price_lakhs: data.extracted.starting_price_lakhs != null ? String(data.extracted.starting_price_lakhs) : "",
        locality_tag: "",
        configurations: "",
        construction_status: "PRE_LAUNCH",
        assigned_agent_slug: "",
      });
      toast.success(
        `Scraped ${new URL(u).hostname}` + (data.extracted.maharera_id ? " · MahaRERA detected" : " · Review before ingest"),
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrape failed");
    } finally {
      setScraping(false);
    }
  };

  const onSwapHeroFromCandidate = (candidate) => setHeroPreview(candidate);

  const onUploadHero = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g|webp)$/i.test(file.name)) return toast.error("PNG / JPG / WEBP only");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5 MB");
    setUploadingHero(true);
    try {
      // Read as data URL so we can preview client-side and send URL string on ingest.
      // For final ingest, the file will be uploaded via the existing image endpoint
      // AFTER the project row is created — for now we just preview.
      const reader = new FileReader();
      reader.onload = () => setHeroPreview(reader.result);
      reader.readAsDataURL(file);
      // Also stash file on the form so we can upload after ingest.
      setForm((f) => ({ ...f, _localHeroFile: file }));
      toast.success("Local image staged — will be uploaded on Final Ingest");
    } finally {
      setUploadingHero(false);
      if (heroRef.current) heroRef.current.value = "";
    }
  };

  const canIngest = useMemo(() => {
    if (!form) return false;
    return form.project_name.trim().length >= 3 && MAHARERA_RE.test(form.maharera_id.trim().toUpperCase()) && !!form.locality_tag;
  }, [form]);

  const onFinalIngest = async () => {
    if (!canIngest) return;
    setIngesting(true);
    try {
      // If admin picked a scraped candidate (not the auto-stored one), pass it through.
      // If they uploaded a local file, ingest without hero and then attach the file.
      const heroToSend = form._localHeroFile ? null : heroPreview;
      const { data } = await api.post("/admin/builder-ingest", {
        project_name: form.project_name.trim(),
        maharera_id: form.maharera_id.trim().toUpperCase(),
        locality_tag: form.locality_tag,
        developer: form.developer.trim() || null,
        starting_price_lakhs: form.starting_price_lakhs === "" ? null : Number(form.starting_price_lakhs),
        configurations: form.configurations.split(",").map((s) => s.trim()).filter(Boolean),
        construction_status: form.construction_status,
        hero_image_url: heroToSend,
        source_url: scrape?.url,
      });
      const pid = data.project.project_id;

      if (form._localHeroFile) {
        // Attach the uploaded file to the freshly-created project.
        const fd = new FormData();
        fd.append("file", form._localHeroFile);
        await api.post(`/admin/master-project/${pid}/image`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const linked = data.retroactive_link?.upgraded || 0;
      toast.success(
        `Ingested ${pid} — ${data.project.project_name}` + (linked ? ` · ${linked} listing${linked === 1 ? "" : "s"} auto-linked` : ""),
        { duration: 5000 },
      );
      // Reset for another URL
      setUrl(""); setScrape(null); setForm(null); setHeroPreview(null);
      navigate(`/admin/ingest-project`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Final ingest failed");
    } finally {
      setIngesting(false);
    }
  };

  return (
    <DashboardShell>
      <div className="overline text-sg-gold-deep">BUILDER FEED · SCRAPE + REVIEW</div>
      <h1 className="font-display text-4xl lg:text-5xl text-sg-navy mt-2">
        Ingest from a <span className="font-serif-italic text-sg-gold-deep">developer URL.</span>
      </h1>
      <p className="text-sg-mute mt-2 max-w-2xl">
        Paste a public developer landing page. We check robots.txt, scrape the project name, MahaRERA ID, price + hero image,
        then let you review + edit before final ingest. Tagged <code className="font-mono bg-sg-cream-soft px-1 rounded">source: builder_feed</code>.
      </p>

      {/* -------- Step 1: URL input -------- */}
      <div className="mt-8 bg-white border border-sg-line rounded-3xl p-6 space-y-3" data-testid="builder-scrape-panel">
        <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5" /> Developer URL
        </Label>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.godrejproperties.com/pune/…"
            className="flex-1 rounded-xl bg-white border-sg-line font-mono"
            data-testid="builder-url-input"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onScrape(); } }}
          />
          <Button
            type="button"
            onClick={onScrape}
            disabled={scraping || !url.trim()}
            className="rounded-full h-11 px-6 bg-sg-navy hover:bg-sg-navy-700 text-white disabled:opacity-50"
            data-testid="builder-scrape-btn"
          >
            {scraping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {scraping ? "Analysing…" : "Scrape & Analyse"}
          </Button>
        </div>
      </div>

      {/* -------- Step 2: Review & edit -------- */}
      {scrape && form && (
        <div className="mt-6 bg-white border border-sg-line rounded-3xl p-6" data-testid="builder-review-panel">
          <div className="overline text-sg-gold-deep">REVIEW · EDIT · INGEST</div>
          <div className="text-sm text-sg-mute mt-1">
            Every field is editable before final ingest. Robots.txt: <span className="text-emerald-700 font-medium">allowed</span>.
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-[minmax(0,200px)_minmax(0,1fr)] gap-6">
            {/* Hero image column */}
            <div>
              <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Hero image</Label>
              <div className="mt-2 aspect-square rounded-xl border border-sg-line bg-sg-cream-soft overflow-hidden flex items-center justify-center" data-testid="builder-hero-preview">
                {heroPreview ? (
                  <img src={heroPreview} alt="Hero preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-xs text-sg-mute p-4 text-center">No hero detected · upload one</div>
                )}
              </div>
              <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-sg-gold-deep hover:text-sg-navy cursor-pointer">
                <input
                  ref={heroRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => onUploadHero(e.target.files?.[0])}
                  data-testid="builder-hero-file-input"
                />
                {uploadingHero ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload different hero
              </label>
              {scrape.hero_image_candidates?.length > 1 && (
                <div className="mt-3">
                  <div className="text-[10px] text-sg-mute uppercase tracking-wide">Or pick a candidate:</div>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {scrape.hero_image_candidates.slice(0, 6).map((c, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => onSwapHeroFromCandidate(c)}
                        className={`aspect-square rounded-lg border overflow-hidden ${heroPreview === c ? "border-sg-gold ring-2 ring-sg-gold/40" : "border-sg-line hover:border-sg-gold/60"}`}
                        data-testid={`builder-hero-candidate-${i}`}
                      >
                        <img src={c} alt={`candidate ${i}`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.4"; }} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Fields column */}
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Project name</Label>
                <Input value={form.project_name} onChange={(e) => setForm((f) => ({ ...f, project_name: e.target.value }))}
                  className="mt-1 rounded-xl bg-white border-sg-line" data-testid="builder-field-project-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">MahaRERA ID</Label>
                  <Input
                    value={form.maharera_id}
                    onChange={(e) => setForm((f) => ({ ...f, maharera_id: e.target.value.toUpperCase() }))}
                    placeholder="P52100012345"
                    className={`mt-1 rounded-xl bg-white font-mono uppercase ${form.maharera_id && !MAHARERA_RE.test(form.maharera_id.toUpperCase()) ? "border-red-300" : "border-sg-line"}`}
                    data-testid="builder-field-rera"
                  />
                  {form.maharera_id && !MAHARERA_RE.test(form.maharera_id.toUpperCase()) && (
                    <div className="text-[10px] text-red-600 mt-0.5">Must match ^P\d{"{8,13}"}$</div>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Developer</Label>
                  <Input value={form.developer} onChange={(e) => setForm((f) => ({ ...f, developer: e.target.value }))}
                    className="mt-1 rounded-xl bg-white border-sg-line" data-testid="builder-field-developer" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Locality</Label>
                  <Select value={form.locality_tag} onValueChange={(v) => setForm((f) => ({ ...f, locality_tag: v }))}>
                    <SelectTrigger className="mt-1 rounded-xl bg-white border-sg-line" data-testid="builder-field-locality">
                      <SelectValue placeholder={scrape.extracted.locality_hint ? `Hint: ${scrape.extracted.locality_hint}` : "Pick a Pune micro-market"} />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_LOCALITIES_WEST.map((l) => <SelectItem key={l} value={l} data-testid={`builder-locality-${l}`}>{l} (Pune West)</SelectItem>)}
                      {ALL_LOCALITIES_EAST.map((l) => <SelectItem key={l} value={l} data-testid={`builder-locality-${l}`}>{l} (Pune East)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Starting price (₹L)</Label>
                  <Input type="number" value={form.starting_price_lakhs}
                    onChange={(e) => setForm((f) => ({ ...f, starting_price_lakhs: e.target.value }))}
                    className="mt-1 rounded-xl bg-white border-sg-line" data-testid="builder-field-price" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Configurations (comma-separated)</Label>
                <Textarea rows={2} value={form.configurations}
                  onChange={(e) => setForm((f) => ({ ...f, configurations: e.target.value }))}
                  className="mt-1 rounded-xl bg-white border-sg-line resize-none" data-testid="builder-field-configs" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Construction status</Label>
                <Select value={form.construction_status} onValueChange={(v) => setForm((f) => ({ ...f, construction_status: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl bg-white border-sg-line" data-testid="builder-field-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONSTRUCTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">
                  Assigned Agent <span className="normal-case font-normal text-sg-mute">(optional — surfaces as &quot;Contact Authorized Agent&quot; on /buy)</span>
                </Label>
                <Select
                  value={form.assigned_agent_slug || NO_AGENT}
                  onValueChange={(v) => setForm((f) => ({ ...f, assigned_agent_slug: v === NO_AGENT ? "" : v }))}
                >
                  <SelectTrigger className="mt-1 rounded-xl bg-white border-sg-line" data-testid="builder-field-assigned-agent">
                    <SelectValue placeholder="Portal-owned (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_AGENT}>Portal-owned (INPropPortal · Verified Launch)</SelectItem>
                    {agents.map((a) => (
                      <SelectItem key={a.slug} value={a.slug}>
                        {a.name || a.email} · /agent/{a.slug}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-sg-line flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-sg-mute">
              Source URL will be saved on the project row for audit.
            </div>
            <Button
              type="button"
              onClick={onFinalIngest}
              disabled={!canIngest || ingesting}
              className="rounded-full h-11 px-6 bg-sg-gold-deep hover:bg-sg-gold text-white disabled:opacity-50"
              data-testid="builder-final-ingest"
            >
              {ingesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {ingesting ? "Ingesting…" : "Final Ingest"}
            </Button>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
